'use client';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ArrowLeftRight, Copy, Check, Info, ChevronDown } from 'lucide-react';
import { BNB_RATES } from '@/app/components/CurrencyCalculatorPage';
import { useLanguage } from '@/context/LanguageContext';
import { getTranslations } from '@/lib/i18n';

type CurrencyKey = 'EUR' | 'USD' | 'BGN';

const CURRENCIES: CurrencyKey[] = ['EUR', 'USD', 'BGN'];

const AMOUNTS = [1, 5, 10, 20, 50, 100, 200, 500, 1000];

function formatNumber(value: number): string {
  if (isNaN(value) || !isFinite(value)) return '—';
  const rounded = Math.round(value * 10000) / 10000;
  const parts = rounded.toFixed(4).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const decimals = parts[1].replace(/0+$/, '').padEnd(2, '0');
  return `${parts[0]}.${decimals}`;
}

function formatNum(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  const parts = rounded.toFixed(2).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${parts[0]}.${parts[1]}`;
}

function parseInput(val: string): number {
  const cleaned = val.replace(/,/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}

function convertAmount(amount: number, from: CurrencyKey, to: CurrencyKey): number {
  if (from === to) return amount;
  const inBGN = from === 'BGN' ? amount : amount * BNB_RATES[from].rateToBGN;
  const result = to === 'BGN' ? inBGN : inBGN / BNB_RATES[to].rateToBGN;
  return result;
}

function getRate(from: CurrencyKey, to: CurrencyKey): number {
  return convertAmount(1, from, to);
}

function getCurrencySymbol(code: CurrencyKey): string {
  return BNB_RATES[code].symbol;
}

function getCurrencyFlag(code: CurrencyKey): string {
  return BNB_RATES[code].flagEmoji;
}

interface CurrencySelectProps {
  value: CurrencyKey;
  onChange: (val: CurrencyKey) => void;
  exclude?: CurrencyKey;
  label: string;
  currencyNames: Record<CurrencyKey, string>;
}

function CurrencySelect({ value, onChange, exclude, label, currencyNames }: CurrencySelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const options = CURRENCIES.filter((c) => c !== exclude);

  const handleSelect = (c: CurrencyKey) => {
    onChange(c);
    setOpen(false);
  };

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
        {label}
      </label>
      <div ref={ref} className="relative">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-border bg-background hover:border-primary transition-all duration-150 focus:outline-none focus:border-primary"
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className="text-xl">{getCurrencyFlag(value)}</span>
          <span className="flex-1 text-left">
            <span className="font-bold text-foreground text-base">{value}</span>
            <span className="ml-2 text-sm text-muted-foreground">{currencyNames[value]}</span>
          </span>
          <span className="text-xl font-bold text-muted-foreground w-7 text-center">
            {getCurrencySymbol(value)}
          </span>
          <ChevronDown
            size={16}
            className={`text-muted-foreground transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
          />
        </button>

        {open && (
          <div className="absolute z-50 top-full left-0 right-0 mt-1 rounded-lg border-2 border-border bg-card shadow-lg overflow-hidden">
            {options.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => handleSelect(c)}
                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-primary/8 hover:text-primary transition-colors duration-100 ${
                  c === value ? 'bg-primary/5 text-primary' : 'text-foreground'
                }`}
              >
                <span className="text-xl">{getCurrencyFlag(c)}</span>
                <span className="flex-1 text-left">
                  <span className="font-bold text-base">{c}</span>
                  <span className="ml-2 text-sm text-muted-foreground">{currencyNames[c]}</span>
                </span>
                <span className="text-xl font-bold text-muted-foreground w-7 text-center">
                  {getCurrencySymbol(c)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function UnifiedConverter() {
  const { language } = useLanguage();
  const t = getTranslations(language);

  const currencyNames: Record<CurrencyKey, string> = {
    EUR: t.currencyEUR,
    USD: t.currencyUSD,
    BGN: t.currencyBGN,
  };

  const [fromCurrency, setFromCurrency] = useState<CurrencyKey>('EUR');
  const [toCurrency, setToCurrency] = useState<CurrencyKey>('BGN');
  const [inputValue, setInputValue] = useState('');
  const [isSwapping, setIsSwapping] = useState(false);
  const [copied, setCopied] = useState(false);
  const [resultKey, setResultKey] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const numericInput = parseInput(inputValue);
  const result = convertAmount(numericInput, fromCurrency, toCurrency);
  const rate = getRate(fromCurrency, toCurrency);
  const rateLabel = `1 ${fromCurrency} = ${formatNumber(rate)} ${toCurrency}`;
  const resultDisplay = numericInput > 0 ? formatNumber(result) : '—';
  const hasResult = numericInput > 0 && result > 0;

  const fromSymbol = getCurrencySymbol(fromCurrency);
  const toSymbol = getCurrencySymbol(toCurrency);

  const handleSwap = useCallback(() => {
    setIsSwapping(true);
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
    setInputValue('');
    setResultKey((k) => k + 1);
    setTimeout(() => setIsSwapping(false), 380);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [fromCurrency, toCurrency]);

  const handleFromChange = (val: CurrencyKey) => {
    if (val === toCurrency) {
      setToCurrency(fromCurrency);
    }
    setFromCurrency(val);
    setInputValue('');
    setResultKey((k) => k + 1);
  };

  const handleToChange = (val: CurrencyKey) => {
    if (val === fromCurrency) {
      setFromCurrency(toCurrency);
    }
    setToCurrency(val);
    setInputValue('');
    setResultKey((k) => k + 1);
  };

  const handleCopy = useCallback(() => {
    if (!hasResult) return;
    const text = `${resultDisplay} ${toCurrency}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [hasResult, resultDisplay, toCurrency]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (/^[0-9]*\.?[0-9]*$/.test(val) || val === '') {
      setInputValue(val);
      setResultKey((k) => k + 1);
    }
  };

  const showMiddle = fromCurrency !== 'BGN' && toCurrency !== 'BGN';
  const middleCurrency: CurrencyKey = 'BGN';

  return (
    <div className="space-y-6">
      {/* Converter Card */}
      <div className="card-sharp shadow-card p-5 sm:p-6 flex flex-col gap-5">
        {/* Rate info */}
        <div className="flex items-center gap-2">
          <Info size={14} className="text-muted-foreground flex-shrink-0" />
          <span className="text-xs font-semibold text-muted-foreground">
            {rateLabel} — {t.officialBNBFixing}
          </span>
        </div>

        {/* Currency selectors + swap */}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-end">
          <CurrencySelect
            value={fromCurrency}
            onChange={handleFromChange}
            label={t.from}
            currencyNames={currencyNames}
          />

          {/* Swap button */}
          <div className="flex justify-center sm:pb-0 pb-1">
            <button
              onClick={handleSwap}
              aria-label={t.swapCurrencies}
              className="flex items-center justify-center w-10 h-10 rounded-full border-2 border-border bg-card hover:border-primary hover:bg-primary/8 hover:text-primary text-muted-foreground transition-all duration-150 active:scale-95 shadow-card"
            >
              <ArrowLeftRight
                size={18}
                className={isSwapping ? 'swap-spin' : ''}
              />
            </button>
          </div>

          <CurrencySelect
            value={toCurrency}
            onChange={handleToChange}
            label={t.to}
            currencyNames={currencyNames}
          />
        </div>

        {/* Amount input */}
        <div>
          <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            {fromCurrency} {t.amount}
          </label>
          <div className="flex items-center gap-3 rounded-lg border-2 border-border px-4 py-3 transition-all duration-150 focus-within:border-primary focus-within:shadow-input-focus bg-background">
            <span className="text-2xl font-bold text-muted-foreground flex-shrink-0 w-8 text-center">
              {fromSymbol}
            </span>
            <input
              ref={inputRef}
              type="number"
              inputMode="decimal"
              placeholder="0.00"
              value={inputValue}
              onChange={handleInputChange}
              className="input-currency flex-1"
              min="0"
              step="any"
              aria-label={`Amount in ${fromCurrency}`}
            />
            <span className="text-sm font-bold text-muted-foreground flex-shrink-0">
              {fromCurrency}
            </span>
          </div>
        </div>

        {/* Result */}
        <div>
          <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            {toCurrency} {t.result}
          </label>
          <div
            className={`flex items-center gap-3 rounded-lg border-2 px-4 py-3 transition-all duration-200 ${
              hasResult ? 'border-accent/40 bg-accent/5' : 'border-border bg-muted/40'
            }`}
          >
            <span className="text-2xl font-bold flex-shrink-0 w-8 text-center text-muted-foreground">
              {toSymbol}
            </span>
            <span
              key={resultKey}
              className={`flex-1 text-2xl font-extrabold tabular-nums ${
                hasResult ? 'text-accent fade-in' : 'text-muted-foreground'
              }`}
              aria-live="polite"
              aria-label={`Result: ${resultDisplay} ${toCurrency}`}
            >
              {resultDisplay}
            </span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-sm font-bold text-muted-foreground">{toCurrency}</span>
              <button
                onClick={handleCopy}
                disabled={!hasResult}
                aria-label={t.copyResult}
                title={t.copyResult}
                className={`flex items-center justify-center w-8 h-8 rounded-md border transition-all duration-150 active:scale-95 ${
                  hasResult
                    ? 'border-accent/30 bg-accent/10 text-accent hover:bg-accent hover:text-white cursor-pointer' :'border-border text-muted-foreground/40 cursor-not-allowed'
                }`}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
          </div>
          {copied && (
            <p className="text-xs text-accent font-semibold mt-1.5 fade-in">{t.copiedToClipboard}</p>
          )}
        </div>

        {/* Conversion note */}
        {hasResult && (
          <div className="text-xs text-muted-foreground bg-muted/60 rounded-md px-3 py-2 fade-in">
            <span className="font-semibold tabular-nums">{inputValue} {fromCurrency}</span>
            {' '}={' '}
            <span className="font-semibold tabular-nums text-foreground">{resultDisplay} {toCurrency}</span>
            {' '}{t.atOfficialRate} ({rateLabel})
          </div>
        )}
      </div>

      {/* Quick Reference Table */}
      <div className="card-sharp shadow-card flex flex-col">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-sm font-bold text-foreground">
            {fromCurrency} ↔ {toCurrency} {t.quickReference}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t.commonAmounts}
          </p>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/60">
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {fromCurrency} ({getCurrencySymbol(fromCurrency)})
                </th>
                {showMiddle && (
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    {middleCurrency} (лв)
                  </th>
                )}
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {toCurrency} ({getCurrencySymbol(toCurrency)})
                </th>
              </tr>
            </thead>
            <tbody>
              {AMOUNTS.map((amount, i) => {
                const toResult = convertAmount(amount, fromCurrency, toCurrency);
                const middleResult = showMiddle ? convertAmount(amount, fromCurrency, 'BGN') : 0;
                return (
                  <tr
                    key={`ref-${fromCurrency}-${toCurrency}-${amount}`}
                    className={`border-t border-border hover:bg-muted/40 transition-colors duration-100 ${
                      i % 2 === 0 ? 'bg-card' : 'bg-muted/20'
                    }`}
                  >
                    <td className="px-4 py-2.5 font-semibold tabular-nums text-foreground">
                      {getCurrencySymbol(fromCurrency)}{formatNum(amount)}
                    </td>
                    {showMiddle && (
                      <td className="px-4 py-2.5 tabular-nums text-muted-foreground">
                        {formatNum(middleResult)} лв
                      </td>
                    )}
                    <td className="px-4 py-2.5 tabular-nums font-bold text-accent">
                      {getCurrencySymbol(toCurrency)}{formatNum(toResult)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-border bg-muted/30 flex items-center justify-between flex-wrap gap-2">
          <p className="text-xs text-muted-foreground">
            1 {fromCurrency} = {formatNum(getRate(fromCurrency, toCurrency))} {toCurrency}
          </p>
          <p className="text-xs text-muted-foreground">
            1 {toCurrency} = {formatNum(getRate(toCurrency, fromCurrency))} {fromCurrency}
          </p>
        </div>
      </div>
    </div>
  );
}
